// Service-worker registration + update orchestration (Implementation Plan v02
// §8.3; Add9 F7). Translates the real ServiceWorker lifecycle into the pure
// `updateReducer` events and exposes a tiny controller to the shell.
//
// Policy enforced here:
//   - check at most every 6-12 h with a random delay, never on every open, so
//     the host's logs do not mirror open times (IR-23);
//   - re-check on `visibilitychange -> visible`, throttled by the same window;
//   - a waiting version is applied ONLY when the page calls `applyNow()`
//     (future S26 "apply now") or at the next cold open — never automatically.

import { swScriptUrl } from './swScriptUrl'
import {
  initialUpdatePhase,
  updateReducer,
  type UpdateEvent,
  type UpdatePhase,
} from './updateState'

export interface UpdateController {
  getPhase(): UpdatePhase
  applyNow(): void
  checkNow(): void
}

export interface RegisterOptions {
  onPhaseChange?: (phase: UpdatePhase) => void
}

const CHECK_FLOOR_MS = 6 * 60 * 60 * 1000 // 6 h
const CHECK_JITTER_MS = 6 * 60 * 60 * 1000 // up to a further 6 h (so 6-12 h)

export function supportsServiceWorker(): boolean {
  return typeof navigator !== 'undefined' && 'serviceWorker' in navigator
}

export async function registerServiceWorker(
  options: RegisterOptions = {},
): Promise<UpdateController | null> {
  // Only run the worker in a real (production) build: in dev it would fight
  // Vite's HMR and cache dev assets. Offline/update behaviour is exercised by
  // the unit tests and on a real device (Batch 5), not by the dev server.
  if (!import.meta.env.PROD || !supportsServiceWorker()) {
    return null
  }

  let phase: UpdatePhase = initialUpdatePhase
  let nextAllowedCheck = 0

  const dispatch = (event: UpdateEvent): void => {
    const next = updateReducer(phase, event)
    if (next !== phase) {
      phase = next
      if (options.onPhaseChange) {
        options.onPhaseChange(phase)
      }
    }
  }

  let registration: ServiceWorkerRegistration
  try {
    // Inside the try: a Trusted Types rejection must land in the same path.
    registration = await navigator.serviceWorker.register(swScriptUrl())
  } catch {
    // A failed registration must never break the app; the shell still runs.
    // It is silent here, so the offline e2e test is tagged @smoke to catch it.
    dispatch({ type: 'error' })
    return null
  }

  const watchInstalling = (worker: ServiceWorker): void => {
    dispatch({ type: 'download-start', version: 'pending' })
    worker.addEventListener('statechange', () => {
      if (worker.state === 'installed' && navigator.serviceWorker.controller) {
        // An update (not the first install) finished installing. In Batch 3 the
        // in-worker signature check is a stub-accept; I-1a verifies for real.
        dispatch({ type: 'verify-ok', version: 'pending' })
        dispatch({ type: 'waiting', version: 'pending' })
      }
    })
  }

  if (registration.waiting && navigator.serviceWorker.controller) {
    dispatch({ type: 'waiting', version: 'pending' })
  }
  registration.addEventListener('updatefound', () => {
    const installing = registration.installing
    if (installing) {
      watchInstalling(installing)
    }
  })

  // The new worker took control (after `applyNow()` or a cold open).
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    dispatch({ type: 'activated', version: 'pending' })
  })

  const runCheck = (): void => {
    const now = Date.now()
    if (now < nextAllowedCheck) {
      return
    }
    nextAllowedCheck = now + CHECK_FLOOR_MS + Math.floor(Math.random() * CHECK_JITTER_MS)
    dispatch({ type: 'check-start' })
    void registration
      .update()
      .then(() => {
        if (!registration.installing && !registration.waiting) {
          dispatch({ type: 'check-none' })
        }
      })
      .catch(() => {
        dispatch({ type: 'error' })
      })
  }

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      runCheck()
    }
  })

  // First check shortly after load (not blocking first paint).
  nextAllowedCheck = 0
  runCheck()

  return {
    getPhase: () => phase,
    applyNow: () => {
      // Explicit, user-initiated. Never called on the worker's own timer.
      if (registration.waiting) {
        registration.waiting.postMessage({ type: 'SKIP_WAITING' })
      }
    },
    checkNow: () => {
      nextAllowedCheck = Math.min(nextAllowedCheck, Date.now())
      runCheck()
    },
  }
}
